import {
  DEFAULT_CONNECTOR_RUNTIME,
  type ConnectorAdminView,
  type ConnectorSeedDocument,
} from "@shared/types/connectorsAdmin";
import { EMPTY_CELL } from "./format";
import { byCodeUnit } from "@shared/sort";

/**
 * A field-by-field comparison of an uploaded seed document against the stored
 * definition (FR-31, canvas 13 panel 05).
 *
 * The SERVER decides whether a document may be applied: it hashes the seed and
 * classifies the result as `insert`, `update`, `skip_no_changes` or
 * `skip_drift`, which is the same code path `npm run seed:connectors` uses, so
 * drift reported in CI and drift reported in this screen cannot disagree. What
 * the server does not return is WHICH fields differ — the classification is a
 * checksum comparison, not a diff — so the list below is computed here, from
 * two things the browser already holds.
 *
 * That makes this a presentation of a difference, never an authority on one. It
 * compares only the fields the admin view carries; the seed's large JSON blocks
 * (`fieldDefinitions`, `credentialValidation`, `entitySelection`, `ui`) are not
 * on that view, so a change confined to them shows as "other fields" rather
 * than being silently reported as no change at all. A diff that quietly omits
 * what it cannot see is worse than one that says so.
 */

export type DriftKind = "Changed" | "Added" | "Removed";

export interface DriftRow {
  id: string;
  kind: DriftKind;
  field: string;
  stored: string;
  inFile: string;
}

function show(value: unknown): string {
  if (value === null || value === undefined || value === "") return EMPTY_CELL;
  if (Array.isArray(value)) return value.length === 0 ? EMPTY_CELL : value.join(", ");
  if (typeof value === "boolean") return value ? "yes" : "no";
  return String(value);
}

function driftKind(stored: string, inFile: string): DriftKind {
  if (stored === EMPTY_CELL) return "Added";
  return inFile === EMPTY_CELL ? "Removed" : "Changed";
}

function compare(
  rows: DriftRow[],
  field: string,
  stored: unknown,
  inFile: unknown,
  id = field,
): void {
  // A key the document omits means "leave it alone", not "clear it": the seed
  // schema makes every tunable optional and the writer falls back to the seed
  // script's defaults, so an absent key is not a removal.
  if (inFile === undefined) return;
  const a = show(stored);
  const b = show(inFile);
  if (a === b) return;
  const kind = driftKind(a, b);
  rows.push({ id, kind, field, stored: a, inFile: b });
}

export function seedDrift(
  stored: ConnectorAdminView | undefined,
  seed: ConnectorSeedDocument,
): DriftRow[] {
  const rows: DriftRow[] = [];
  if (!stored) {
    return [
      {
        id: "new",
        kind: "Added",
        field: "connector",
        stored: EMPTY_CELL,
        inFile: `${seed.displayName} (${seed.name})`,
      },
    ];
  }

  compare(rows, "displayName", stored.displayName, seed.displayName);
  compare(rows, "description", stored.description, seed.description);
  compare(rows, "kind", stored.kind, seed.kind);
  compare(rows, "version", stored.version, seed.version);
  compare(rows, "categories", stored.categories, seed.categories);
  compare(rows, "iconUrl", stored.iconUrl, seed.iconUrl);
  compare(rows, "isPreview", stored.isPreview, seed.isPreviewEnabled);
  compare(rows, "pipelineTemplate", stored.pipelineTemplate, seed.pipelineTemplate);
  // Not a tunable: an absent declaration is what the upsert writes as `legacy_script` (0018).
  compare(rows, "runtime", stored.runtime, seed.runtime ?? DEFAULT_CONNECTOR_RUNTIME);
  compare(rows, "runTimeoutSeconds", stored.runTimeoutSeconds, seed.runTimeoutSeconds);
  compare(rows, "pollIntervalSeconds", stored.pollIntervalSeconds, seed.pollIntervalSeconds);
  compare(rows, "cpu", stored.cpu, seed.cpu);
  compare(rows, "memoryGb", stored.memoryGb, seed.memoryGb);
  compare(rows, "maxConcurrentRuns", stored.maxConcurrentRuns, seed.maxConcurrentRuns);
  compare(
    rows,
    "isIncrementalSupported",
    stored.isIncrementalSupported,
    seed.isIncrementalSupported,
  );
  compare(rows, "incrementalGoBackDays", stored.incrementalGoBackDays, seed.incrementalGoBackDays);
  compare(
    rows,
    "egressFqdns",
    [...stored.egressFqdns].sort(byCodeUnit),
    [...(seed.egressFqdns ?? [])].sort(byCodeUnit),
  );
  compare(rows, "imageName", stored.imageName, seed.connectorImageName);

  if (seed.connectorImages) {
    const storedTags = stored.versions.map((version) => version.tag).sort(byCodeUnit);
    const seedTags = seed.connectorImages.map((image) => image.tag).sort(byCodeUnit);
    compare(rows, "image tags", storedTags, seedTags);
  }

  if (seed.entities) {
    compare(
      rows,
      "entities",
      `${stored.entityCount} declared entities`,
      `${seed.entities.length} declared entities`,
    );
  }

  return rows;
}

/** The JSON blocks this comparison cannot see, named so their absence is explicit. */
export const UNCOMPARED_BLOCKS = [
  "fieldGroups",
  "fieldDefinitions",
  "credentialValidation",
  "entitySelection",
  "ui",
] as const;

export function documentCarriesUncomparedBlocks(seed: ConnectorSeedDocument): boolean {
  const record = seed as unknown as Record<string, unknown>;
  return UNCOMPARED_BLOCKS.some((key) => record[key] !== undefined);
}
